/* eslint-disable no-restricted-syntax */
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { capitalCase } from 'change-case';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Card, Grid, Avatar, Stack, Switch, Container, Typography, FormControlLabel } from '@mui/material';
// utils
import { fData } from '../../../../../utils/formatNumber';
// hooks
import useSettings from '../../../../../hooks/useSettings';
// routes
import { PATH_ACCOUNT, PATH_DASHBOARD, PATH_SUPPORT, PATH_TRANSACTION } from '../../../../../routes/paths';

import Page from '../../../../../components/Page';
import HeaderBreadcrumbs from '../../../../../components/HeaderBreadcrumbs';
// _mock
import { countries } from '../../../../../_mock';
// components
import Label from '../../../../../components/Label';
import { FormProvider, RHFSelect, RHFSwitch, RHFTextField, RHFCheckbox } from '../../../../../components/hook-form';
import { adminApi } from '../../../../../utils/axios';
import { USER_SUPPORT_CATEGORY } from '../../../../../utils/apiUrl';
import Iconify from '../../../../../components/Iconify';

// ----------------------------------------------------------------------

export default function UserSupportCategoryCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStretch } = useSettings();

  const { enqueueSnackbar } = useSnackbar();

  const { currentAgent, isEdit } = location.state;

  const NewTransactionSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
  });
  const [isLoading, setLoading] = useState(false);

  const defaultValues = useMemo(
    () => ({
      name: currentAgent?.name || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentAgent]
  );

  const methods = useForm({
    resolver: yupResolver(NewTransactionSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentAgent) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentAgent]);

  const handleArchive = async () => {
    const confirmBox = window.confirm('Do you really want to archive this record?');
    if (confirmBox) {
      try {
        const response = await adminApi.delete(`${USER_SUPPORT_CATEGORY}${currentAgent?.id}/`);
        enqueueSnackbar(response?.data?.message || `Data deleted successfuly!`);
        navigate(-1);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (isEdit) {
        adminApi
          .patch(`${USER_SUPPORT_CATEGORY}${currentAgent.id}/`, data)
          .then(({ data }) => {
            reset();
            setLoading(false);
            enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
            navigate(PATH_SUPPORT.support.userSupport.supportCategories.root);
          })
          .catch((error) => {
            if (typeof error === 'object') {
              setLoading(false);
              for (const [key, value] of Object.entries(error)) {
                enqueueSnackbar(`${key}: ${value}`, { variant: 'error' });
              }
            } else {
              setLoading(false);
              enqueueSnackbar(error ?? 'Something went wrong', { variant: 'error' });
            }
          });
      } else {
        adminApi
          .post(USER_SUPPORT_CATEGORY, data)
          .then(({ data }) => {
            reset();
            setLoading(false);
            enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
            navigate(PATH_SUPPORT.support.userSupport.supportCategories.root);
          })
          .catch((error) => {
            if (typeof error === 'object') {
              setLoading(false);
              for (const [key, value] of Object.entries(error)) {
                enqueueSnackbar(`${key}: ${value}`, { variant: 'error' });
              }
            } else {
              setLoading(false);
              enqueueSnackbar(error ?? 'Something went wrong', { variant: 'error' });
            }
          });
      }
    } catch (error) {
      // console.error(error);
    }
  };

  return (
    <Page title={!isEdit ? 'Create Support Category' : 'Edit Support Category'}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: '10%' }}
          onClick={() => navigate(-1)}
        >
          <Iconify icon={'ion:arrow-back'} width={30} height={20} />
          <Typography>Back</Typography>
        </Box>
        <HeaderBreadcrumbs
          heading={!isEdit ? 'Create Support Category' : 'Edit Support Category'}
          links={[
            { name: 'Support Category', href: PATH_SUPPORT.support.userSupport.supportCategories.root },
            { name: 'List', href: PATH_SUPPORT.support.userSupport.supportCategories.root },
            { name: !isEdit ? 'New Support Category' : 'Edit Support Category' },
          ]}
        />

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2} />

            <Grid item xs={12} md={8}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                  }}
                >
                  <RHFTextField name="name" label="Name" />
                </Box>

                <Stack alignItems="flex-end" spacing={3} sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {isEdit && (
                      <LoadingButton variant="contained" color={'error'} onClick={handleArchive}>
                        Archive
                      </LoadingButton>
                    )}
                    <LoadingButton type="submit" variant="contained" loading={isLoading}>
                      {!isEdit ? 'Create Category' : 'Save Changes'}
                    </LoadingButton>
                  </Box>
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} md={2} />
          </Grid>
        </FormProvider>
      </Container>
    </Page>
  );
}
